import { Vector3 } from '@babylonjs/core';
import { Control, Grid } from '@babylonjs/gui';
import { Game } from './Game';
import { GameConfig } from './GameConfig';
import { GameState } from './GameState';
import { Gui } from './gui/Gui';

interface HighscoreEntry {
  name: string;
  totalScore: number;
}

export class Highscores {
  constructor(public game: Game) {}

  async create() {
    await Gui.createDefaultScene(
      this.game,
      {
        url: 'assets/skybox/skybox_fantasy_castle.glb',
        rotation: new Vector3(-Math.PI / 2, 0, 0),
        rotationAxis: 'y',
      },
      '🏆 Global Highscores'
    );
    await Gui.createBackButton(this.game, GameState.MAIN, '🔙');

    await this.game.sound.createBackgroundMusic('SongwriterBallad');

    const container = this.game.gui2D.createRectangle('80%', '60%', this.game.gui2D.ui.rootContainer);
    container.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_CENTER;
    container.verticalAlignment = Control.VERTICAL_ALIGNMENT_CENTER;
    container.top = '12%';
    container.thickness = 0;
    container.background = Gui.controlBackground;
    container.cornerRadius = 20;

    const loadingText = this.game.gui2D.createTextBlock('loading_text', 'Loading Highscores...', container, Gui.fontSizeXXXXL);
    loadingText.color = 'white';
    loadingText.textHorizontalAlignment = Control.HORIZONTAL_ALIGNMENT_CENTER;

    try {
      const response = await fetch(`${GameConfig.serverUrl}/highscores`);
      if (response.ok) {
        const data = await response.json();
        const leaderboard: HighscoreEntry[] = data.leaderboard || [];

        container.removeControl(loadingText);

        if (leaderboard.length === 0) {
          const noDataText = this.game.gui2D.createTextBlock('no_data_text', 'No highscores yet!', container, Gui.fontSizeXXXXL);
          noDataText.color = 'white';
          noDataText.textHorizontalAlignment = Control.HORIZONTAL_ALIGNMENT_CENTER;
        } else {
          const grid = new Grid('highscores_grid');
          grid.width = '90%';
          grid.verticalAlignment = Control.VERTICAL_ALIGNMENT_TOP;
          grid.paddingTop = '20px';

          grid.addColumnDefinition(0.2); // Rank
          grid.addColumnDefinition(0.5); // Name
          grid.addColumnDefinition(0.3); // Score

          // Limit to top 10 for UI layout simplicity
          const displayCount = Math.min(10, leaderboard.length);
          const rowHeight = 56;
          grid.heightInPixels = rowHeight * (displayCount + 1);

          for (let i = 0; i <= displayCount; i++) {
            grid.addRowDefinition(rowHeight, true);
          }

          // Headers
          const headers = ['Rank', 'Player Name', 'Total Score'];
          headers.forEach((text, i) => {
            const header = this.game.gui2D.createTextBlock(`hs_header_${i}`, text, undefined, Gui.fontSizeXXXXL);
            header.color = Gui.secondaryColor.toHexString();
            header.textHorizontalAlignment = i === 1 ? Control.HORIZONTAL_ALIGNMENT_LEFT : Control.HORIZONTAL_ALIGNMENT_CENTER;
            grid.addControl(header, 0, i);
          });

          // Rows
          leaderboard.slice(0, 10).forEach((entry, i) => {
            const rank = this.game.gui2D.createTextBlock(`hs_rank_${i}`, `#${i + 1}`, undefined, Gui.fontSizeXXXXL);
            rank.color = i === 0 ? '#FFD700' : i === 1 ? '#C0C0C0' : i === 2 ? '#CD7F32' : 'white';
            rank.textHorizontalAlignment = Control.HORIZONTAL_ALIGNMENT_CENTER;
            grid.addControl(rank, i + 1, 0);

            const name = this.game.gui2D.createTextBlock(`hs_name_${i}`, entry.name, undefined, Gui.fontSizeXXXXL);
            name.color = 'white';
            name.textHorizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT;
            grid.addControl(name, i + 1, 1);

            const score = this.game.gui2D.createTextBlock(`hs_score_${i}`, entry.totalScore.toString(), undefined, Gui.fontSizeXXXXL);
            score.color = '#00BBFF';
            score.textHorizontalAlignment = Control.HORIZONTAL_ALIGNMENT_CENTER;
            grid.addControl(score, i + 1, 2);
          });

          container.addControl(grid);
        }
      } else {
        loadingText.text = 'Failed to load highscores';
        loadingText.color = 'red';
      }
    } catch (error) {
      console.error('Highscore loading error:', error);
      loadingText.text = 'Could not connect to server';
      loadingText.color = 'red';
    }
  }
}
